import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import { Box } from '@mui/system';
import {
  Card,
  Grid,
  List,
  Badge,
  Stack,
  Button,
  ListItem,
  useTheme,
  TextField,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
  ListItemText,
  useMediaQuery
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import {
  useGetOrgNoSyncListQuery,
  useUpdateOrgNoSyncListMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import FormProvider from 'src/components/hook-form';
import EmptyContent from 'src/components/empty-content';
import ListSkeleton from 'src/components/list/list-skeleton';

export default function EmailSyncSettingsCard() {

  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const authContext = useAuthContext();

  const { currentData: noSyncList, isLoading: isLoadingSettings } = useGetOrgNoSyncListQuery(
    authContext.organization.id
  );

  const [updateSettings, { isLoading: isUpdating }] = useUpdateOrgNoSyncListMutation();

  // Validation schema for a single email/domain entry
  const emailDomainValidation = Yup.string()
    .matches(
      /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,})$/,
      t('admin-organization.settings.email_sync_settings.validation.invalid_format')
    )
    .required(t('validation.required'));

  const EmailSyncSettingsSchema = Yup.object().shape({
    no_sync_list: Yup.array().required(t('validation.required')),
  });

  const methods = useForm({
    resolver: yupResolver(EmailSyncSettingsSchema),
    defaultValues: {
      no_sync_list: [],
    },
  });

  const {
    setValue,
    reset,
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValid },
  } = methods;

  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');

  useEffect(() => {
    if (Array.isArray(noSyncList)) {
      reset({
        no_sync_list: noSyncList
      });
    }
  }, [noSyncList, reset]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setInputError('');
  };

  const handleAddItem = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      setInputError(t('validation.required'));
      return;
    }

    try {
      // Validate the input
      emailDomainValidation.validateSync(trimmedValue);

      // Check for duplicates
      const currentList = getValues('no_sync_list');
      if (currentList.includes(trimmedValue)) {
        setInputError(t('validation.unique'));
        return;
      }

      // Add the item to the list
      const updatedList = [...currentList, trimmedValue];
      setValue('no_sync_list', updatedList, { shouldValidate: true });

      onSubmit(); // Trigger submit to update settings

      // Clear the input
      setInputValue('');
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setInputError(error.message);
      }
    }
  };

  // Handle removing an item
  const handleRemoveItem = (index: number) => {
    const currentList = getValues('no_sync_list');
    const updatedList = [...currentList];
    updatedList.splice(index, 1);
    setValue('no_sync_list', updatedList, { shouldValidate: true });
    onSubmit(); // Trigger submit to update settings
  };

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      const itemsToUpdate = data.no_sync_list.filter((text: string) => text !== '');

      await updateSettings({
        organizationId: authContext.organization.id,
        noSyncItems: itemsToUpdate,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.email_sync_settings.api.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.email_sync_settings.api.update.default_error'), { variant: 'error' });
    }
  });

  const canEditSettings = useMemo(() => {
    if (!authContext?.account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role);
  }, [authContext])

  return (
    <FormProvider methods={methods}>
      <Card sx={{ width: '100%', boxShadow: 0 }}>
        <CardHeader
          avatar={
            <Iconify
              icon="fluent:mail-settings-16-regular"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6">{t('admin-organization.settings.email_sync_settings.title')}</Typography>
          }
          subheader={t('admin-organization.settings.email_sync_settings.description')}
        />

        < CardContent >
          <Stack spacing={2} sx={{ px: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack direction="column" my={2} mb={4}>
                  <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
                    <Stack direction="row" gap={2} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t('admin-organization.settings.email_sync_settings.emails_to_not_sync')}
                      </Typography>

                      {getValues('no_sync_list')?.length > 0 && (
                        <Badge badgeContent={getValues('no_sync_list')?.length} color="primary" />
                      )}
                    </Stack>
                  </Stack>

                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('admin-organization.settings.email_sync_settings.emails_to_not_sync_helper')}
                  </Typography>
                </Stack>

                {/* Input field with add button */}
                <Stack direction="row" spacing={2} mb={3} alignItems="start">
                  <TextField
                    fullWidth
                    value={inputValue}
                    onChange={handleInputChange}
                    label={t('admin-organization.settings.email_sync_settings.text_label')}
                    error={!!inputError}
                    disabled={!canEditSettings || isLoadingSettings || isUpdating}
                    helperText={inputError}
                    placeholder="example@domain.com or domain.com"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddItem();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <Button
                          variant="contained"
                          onClick={handleAddItem}
                          startIcon={<Iconify icon="formkit:add" />}
                          sx={{ minWidth: '120px' }}
                        >
                          {t('common.add')}
                        </Button>
                      ),
                    }}

                  />


                </Stack>

                {
                  isLoadingSettings ? (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <ListSkeleton />
                    </Box>
                  )
                    :
                    (
                      <>
                        {/* List of added items */}
                        {getValues('no_sync_list')?.length > 0 ? (
                          <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                            {getValues('no_sync_list').map((item: string, index: number) => (
                              <ListItem
                                key={`${item}-${index}`}
                                secondaryAction={
                                  <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRemoveItem(index)}
                                  >
                                    <Iconify icon="bi:trash" />
                                  </IconButton>
                                }
                                divider={index < getValues('no_sync_list').length - 1}
                              >
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Stack>
                            <EmptyContent />
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {t('admin-organization.settings.email_sync_settings.no_emails')}
                              </Typography>
                            </Box>
                          </Stack>
                        )}
                      </>
                    )
                }

              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </FormProvider >
  );
}